import { useAccessRestrictionContext } from '@/context/AccessRestrictionContext';
import { useAccessRestriction, type RestrictionReason } from '@/hooks/useAccessRestriction';
import { FullPageSpinner } from '@/components/ui';
import { useLocation } from 'react-router';
import { Clock, Lock, ShieldOff } from 'lucide-react';
import type { ReactNode } from 'react';
import { SeatStatus } from '@/services/seatAssignmentService';
import { useTranslation } from 'react-i18next';

const GATE_EXEMPT_PREFIXES = ['/account-settings', '/profile'];

interface ModuleGateProps {
  moduleKey: string;
  children: ReactNode;
  skipSubscriptionCheck?: boolean;
}

function BlockScreen({ icon: Icon, title, description }: { icon: typeof Lock; title: string; description: string }) {
  return (
    <div className='min-h-[60vh] flex items-center justify-center p-8'>
      <div className='max-w-sm w-full text-center'>
        <div className='mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-muted'>
          <Icon className='h-8 w-8 text-muted-foreground' />
        </div>
        <h2 className='text-xl font-semibold text-foreground mb-2'>{title}</h2>
        <p className='text-sm text-muted-foreground leading-relaxed'>{description}</p>
      </div>
    </div>
  );
}

export function SubscriptionInactiveScreen({ reason }: { reason?: RestrictionReason } = {}) {
  const { t } = useTranslation();
  if (reason === SeatStatus.RESERVED) {
    return (
      <BlockScreen
        icon={Clock}
        title={t('accessRestriction.gate.reservedTitle')}
        description={t('accessRestriction.gate.reservedDescription')}
      />
    );
  }
  return (
    <BlockScreen
      icon={Lock}
      title={t('accessRestriction.gate.unassignedTitle')}
      description={t('accessRestriction.gate.unassignedDescription')}
    />
  );
}

function TierInsufficientScreen() {
  const { t } = useTranslation();
  return (
    <BlockScreen
      icon={ShieldOff}
      title={t('accessRestriction.gate.tierInsufficientTitle')}
      description={t('accessRestriction.gate.tierInsufficientDescription')}
    />
  );
}

export function ModuleGate({ moduleKey, children, skipSubscriptionCheck = false }: ModuleGateProps) {
  const { moduleAccess, moduleAccessLoading, subscriptionActive } = useAccessRestrictionContext();
  const { isRestricted, restrictionReason } = useAccessRestriction();

  if (moduleAccessLoading) {
    return <FullPageSpinner />;
  }

  if (!skipSubscriptionCheck && (isRestricted || !subscriptionActive)) {
    return <SubscriptionInactiveScreen reason={restrictionReason} />;
  }

  if (!moduleAccess(moduleKey)) {
    return <TierInsufficientScreen />;
  }

  return <>{children}</>;
}

export function SubscriptionGate({ children }: { children: ReactNode }) {
  const { moduleAccessLoading } = useAccessRestrictionContext();
  const { isRestricted, restrictionReason } = useAccessRestriction();
  const { pathname } = useLocation();

  if (moduleAccessLoading) {
    return <FullPageSpinner />;
  }

  const isExempt = GATE_EXEMPT_PREFIXES.some((prefix) => pathname.startsWith(prefix));
  if (isExempt) {
    return <>{children}</>;
  }

  if (isRestricted) {
    return <SubscriptionInactiveScreen reason={restrictionReason} />;
  }

  return <>{children}</>;
}
